import { useEffect, useState } from "react";
import { X, Download, Search, Activity, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { API, openDownload } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function MiniChart({ trace, events }) {
  if (!trace || trace.length < 2) return <div className="text-slate-500 text-sm p-6 text-center">Brak danych trasy w pliku .sor</div>;
  const W = 760, H = 240, pad = 34;
  const xs = trace.map((p) => p[0]), ys = trace.map((p) => p[1]);
  const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
  const xr = (xmax - xmin) || 1, yr = (ymax - ymin) || 1;
  const sx = (x) => pad + (x - xmin) / xr * (W - pad - 12);
  const sy = (y) => H - pad - (y - ymin) / yr * (H - pad - 12);
  const dpath = trace.map((p, i) => `${i ? "L" : "M"}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`).join(" ");
  const evLines = (events || []).map((ev, i) => {
    const ex = parseFloat(String(ev.distance).replace(",", "."));
    if (isNaN(ex) || ex < xmin || ex > xmax) return null;
    const x = sx(ex);
    return (<g key={i}><line x1={x} y1={12} x2={x} y2={H - pad} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><text x={x + 2} y={22} fill="#fbbf24" fontSize="9">{ex.toFixed(2)}</text></g>);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="otdr-explorer-chart">
      <rect x="0" y="0" width={W} height={H} fill="#0b1428" stroke="#334155" />
      <line x1={pad} y1={H - pad} x2={W - 12} y2={H - pad} stroke="#475569" />
      <line x1={pad} y1={12} x2={pad} y2={H - pad} stroke="#475569" />
      <path d={dpath} fill="none" stroke="#06b6d4" strokeWidth="1" />
      {evLines}
      <text x={pad} y={H - 8} fill="#94a3b8" fontSize="11">{xmin.toFixed(2)} km</text>
      <text x={W - 70} y={H - 8} fill="#94a3b8" fontSize="11">{xmax.toFixed(2)} km</text>
      <text x="4" y="20" fill="#94a3b8" fontSize="11">{ymax.toFixed(1)} dB</text>
      <text x="4" y={H - pad} fill="#94a3b8" fontSize="11">{ymin.toFixed(1)} dB</text>
    </svg>
  );
}

export default function OtdrExplorer({ onClose }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/otdr", { params: { q: q || undefined } }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = items.filter((m) => status === "all" || m.status === status);
  const _withLoss = items.filter((m) => typeof m.total_loss_db === "number");
  const _avgLoss = _withLoss.length ? _withLoss.reduce((s, m) => s + m.total_loss_db, 0) / _withLoss.length : null;
  const _failCount = items.filter((m) => m.status === "fail").length;
  const _okCount = items.filter((m) => m.status === "ok").length;
  const color = (s) => (s === "ok" ? "text-green-400" : s === "fail" ? "text-red-400" : "text-amber-400");
  const dot = (s) => (s === "ok" ? "bg-green-400" : s === "fail" ? "bg-red-400" : "bg-amber-400");
  const dl = (m) => openDownload(`/otdr/${m.id}/download`);
  const exportSummary = async (fmt) => {
    try {
      const r = await api.get("/otdr/export", { params: { format: fmt, q: q || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `otdr_podsumowanie_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(t("Wyeksportowano podsumowanie OTDR"));
    } catch (e) { toast.error(t("Nie udało się wyeksportować")); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="otdr-explorer">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-gradient-to-r from-cyan-900/40 to-slate-900">
        <div className="flex items-center gap-2"><Activity size={18} className="text-cyan-400" /><h2 className="font-heading font-bold text-base">{t("Pomiary OTDR")}</h2>
          <span className="text-xs text-slate-400">({filtered.length})</span></div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportSummary("csv")} disabled={items.length === 0} data-testid="otdr-export-csv" title={t("Eksport podsumowania CSV")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-800 border border-slate-700 text-green-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"><FileSpreadsheet size={15} /> CSV</button>
          <button onClick={() => exportSummary("pdf")} disabled={items.length === 0} data-testid="otdr-export-pdf" title={t("Eksport podsumowania PDF")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-800 border border-slate-700 text-red-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"><FileText size={15} /> PDF</button>
          <button onClick={onClose} data-testid="otdr-explorer-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
      </div>

      <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="otdr-explorer-search"
            placeholder={t("Szukaj: kabel, plik, włókno...")} className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="otdr-explorer-status" className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white">
          <option value="all">{t("Wszystkie")}</option>
          <option value="ok">{t("W budżecie")}</option>
          <option value="fail">{t("Przekroczone")}</option>
          <option value="unknown">{t("Bez metryk")}</option>
        </select>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white">{t("Odśwież")}</button>
      </div>

      {!loading && items.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="otdr-explorer-stats">
          <div className="bg-slate-800/60 rounded-lg px-3 py-2">
            <div className="text-lg font-mono font-bold text-white" data-testid="otdr-stat-total">{items.length}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{t("Pomiary")}</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg px-3 py-2">
            <div className="text-lg font-mono font-bold text-cyan-300" data-testid="otdr-stat-avg-loss">{_avgLoss != null ? `${_avgLoss.toFixed(2)} dB` : "—"}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{t("Średnie tłumienie")}</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg px-3 py-2">
            <div className="text-lg font-mono font-bold text-red-400" data-testid="otdr-stat-fail">{_failCount}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{t("Przekroczenia budżetu")}</div>
          </div>
          <div className="bg-slate-800/60 rounded-lg px-3 py-2">
            <div className="text-lg font-mono font-bold text-green-400" data-testid="otdr-stat-ok">{_okCount}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{t("W budżecie")}</div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading ? <div className="p-6 text-slate-500 text-sm">{t("Ładowanie...")}</div> : filtered.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">{t("Brak pomiarów OTDR")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-[#0e1830] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">{t("Kabel")}</th><th className="text-left px-3 py-2">{t("Wł.")}</th>
                <th className="text-left px-3 py-2">{t("Fala")}</th><th className="text-left px-3 py-2">{t("Długość")}</th>
                <th className="text-left px-3 py-2">{t("Tłumienie")}</th><th className="text-left px-3 py-2">{t("Zdarz.")}</th>
                <th className="text-left px-3 py-2">{t("Plik")}</th><th className="text-left px-3 py-2">{t("Data")}</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} onClick={() => setSel(m)} data-testid={`otdr-row-${m.id}`} className="border-t border-slate-800/70 text-slate-300 hover:bg-slate-800/50 cursor-pointer">
                  <td className="px-4 py-2 font-medium text-white">{m.cable_number || m.cable_id}</td>
                  <td className="px-3 py-2">{m.fiber}</td>
                  <td className="px-3 py-2">{m.wavelength || "—"}</td>
                  <td className="px-3 py-2">{m.total_length_m != null ? `${m.total_length_m} m` : "—"}</td>
                  <td className={`px-3 py-2 font-medium ${color(m.status)}`}><span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${dot(m.status)}`}></span>{m.total_loss_db != null ? `${m.total_loss_db} dB` : "—"}</td>
                  <td className="px-3 py-2">{m.num_events != null ? m.num_events : "—"}</td>
                  <td className="px-3 py-2 text-slate-400 truncate max-w-[180px]" title={m.original_filename}>{m.original_filename}</td>
                  <td className="px-3 py-2 text-slate-500">{String(m.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2"><button onClick={(e) => { e.stopPropagation(); dl(m); }} data-testid={`otdr-row-dl-${m.id}`} className="text-slate-400 hover:text-cyan-300"><Download size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sel && (
        <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/80 p-6" data-testid="otdr-explorer-preview" onClick={() => setSel(null)}>
          <div className="bg-[#0b1220] border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
              <div><div className="text-xs text-slate-500">{t("Reflektogram OTDR")}</div>
                <div className="text-base font-heading font-bold text-white">{sel.cable_number || sel.cable_id} · {t("wł.")} {sel.fiber}<span className="text-slate-400 font-normal text-sm"> · {sel.original_filename}</span></div></div>
              <div className="flex items-center gap-2">
                <button onClick={() => dl(sel)} className="text-slate-300 hover:text-cyan-300" title={t("Pobierz .sor")}><Download size={18} /></button>
                <button onClick={() => setSel(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto">
              <div className="grid grid-cols-4 gap-2 mb-4 text-center">
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{sel.wavelength || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Fala")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{sel.total_length_m != null ? `${sel.total_length_m} m` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Długość")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className={`text-sm font-mono ${color(sel.status)}`}>{sel.total_loss_db != null ? `${sel.total_loss_db} dB` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Tłumienie")}</div></div>
                <div className="bg-slate-800/60 rounded-lg py-2"><div className="text-sm font-mono text-white">{sel.num_events != null ? sel.num_events : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Zdarzenia")}</div></div>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4 text-center">
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{sel.range_m != null ? `${sel.range_m} m` : "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Zakres")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{sel.pulse_width || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Impuls")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{sel.ior || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Wsp. załamania")}</div></div>
                <div className="bg-slate-800/40 rounded-lg py-1.5"><div className="text-xs font-mono text-slate-200">{sel.fiber_type || "—"}</div><div className="text-[10px] text-slate-500 uppercase">{t("Typ włókna")}</div></div>
              </div>
              <MiniChart trace={sel.trace} events={sel.events} />
              {(sel.events || []).length > 0 && (
                <table className="w-full text-sm mt-4 rounded-lg overflow-hidden border border-slate-800" data-testid="otdr-explorer-events">
                  <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-[#0e1830]"><tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">{t("Typ")}</th><th className="text-left px-3 py-2">{t("Odległość")}</th><th className="text-left px-3 py-2">{t("Tłumienie")}</th><th className="text-left px-3 py-2">{t("Odbicie")}</th></tr></thead>
                  <tbody>{sel.events.map((ev) => (<tr key={ev.n} className="border-t border-slate-800/70 text-slate-300"><td className="px-3 py-1.5">{ev.n}</td><td className="px-3 py-1.5">{ev.type || "—"}</td><td className="px-3 py-1.5">{ev.distance || "—"}</td><td className="px-3 py-1.5">{ev.splice_loss || "—"}</td><td className="px-3 py-1.5">{ev.refl_loss || "—"}</td></tr>))}</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
